import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Plus, Search, Edit, Trash2, KeyRound, Copy, ShieldCheck, Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { clientApi, Client, clientAccountApi, ClientAccount } from "@/services/api";

const PASSWORD_MIN_LENGTH = 8;

const Accounts = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [accounts, setAccounts] = useState<ClientAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [selectedClient, setSelectedClient] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [editingAccount, setEditingAccount] = useState<ClientAccount | null>(null);

  // One-time password reveal dialog (after create or reset)
  const [showRevealDialog, setShowRevealDialog] = useState(false);
  const [revealAccount, setRevealAccount] = useState<ClientAccount | null>(null);
  const [revealPassword, setRevealPassword] = useState("");
  const [revealMode, setRevealMode] = useState<'created' | 'reset'>('created');

  const [accountToDelete, setAccountToDelete] = useState<ClientAccount | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [accountToReset, setAccountToReset] = useState<ClientAccount | null>(null);
  const [resetting, setResetting] = useState(false);

  // On-demand password reveal in the table (decrypted from server vault)
  const [revealedPasswords, setRevealedPasswords] = useState<Record<string, string>>({});
  const [revealingId, setRevealingId] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [clientsData, accountsData] = await Promise.all([
          clientApi.getAll(),
          clientAccountApi.getAll(),
        ]);
        setClients(clientsData);
        setAccounts(accountsData);
      } catch (err) {
        console.error('Failed to load data:', err);
        toast({
          title: "Error",
          description: "Failed to load clients and accounts.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const getAccountClientId = (account: ClientAccount): string => {
    if (!account || account.client == null) return '';
    return typeof account.client === 'string' ? account.client : account.client._id;
  };

  const getAccountClientName = (account: ClientAccount): string => {
    if (!account || account.client == null) return 'Unknown';
    if (typeof account.client !== 'string') return account.client?.name || 'Unknown';
    const client = clients.find(c => c._id === account.client);
    return client ? client.name : 'Unknown';
  };

  const generateStrongPassword = () => {
    const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
    const lower = "abcdefghijkmnopqrstuvwxyz";
    const digits = "23456789";
    const symbols = "!@#$%^&*()_-+=[]{}";
    const all = upper + lower + digits + symbols;
    const pick = (chars: string, n: number) =>
      Array.from({ length: n }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
    const base = pick(upper, 2) + pick(lower, 4) + pick(digits, 2) + pick(symbols, 2);
    const remaining = pick(all, 4);
    const raw = (base + remaining).split("");
    for (let i = raw.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [raw[i], raw[j]] = [raw[j], raw[i]];
    }
    return raw.join("");
  };

  const validatePassword = (pw: string): string | null => {
    if (!pw || pw.length < PASSWORD_MIN_LENGTH) {
      return `Password must be at least ${PASSWORD_MIN_LENGTH} characters`;
    }
    return null;
  };

  const openRevealDialog = (account: ClientAccount, plaintext: string, mode: 'created' | 'reset') => {
    setRevealAccount(account);
    setRevealPassword(plaintext);
    setRevealMode(mode);
    setShowRevealDialog(true);
  };

  const handleCreateAccount = async () => {
    if (!selectedClient || !email || !password) {
      setError("Please fill in all required fields.");
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    const existingAccount = accounts.find(acc => getAccountClientId(acc) === selectedClient);
    if (existingAccount) {
      setError("An account already exists for this client.");
      return;
    }

    const emailExists = accounts.find(acc => acc.email.toLowerCase() === email.toLowerCase());
    if (emailExists) {
      setError("This email is already in use by another client.");
      return;
    }

    const client = clients.find(c => c._id === selectedClient);
    if (!client) {
      setError("Selected client not found.");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const created = await clientAccountApi.create({
        clientId: selectedClient,
        email: email.toLowerCase(),
        password,
      });
      setAccounts(prev => [...prev, created]);
      if (created._id) {
        setRevealedPasswords(prev => ({ ...prev, [created._id!]: password }));
      }

      const plaintext = password;
      setSelectedClient("");
      setEmail("");
      setPassword("");
      setShowCreateDialog(false);

      openRevealDialog(created, plaintext, 'created');
    } catch (err: any) {
      setError(err?.message || "Failed to create account. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleEditAccount = (account: ClientAccount) => {
    setEditingAccount(account);
    setSelectedClient(getAccountClientId(account));
    setEmail(account.email);
    setPassword("");
    setError(null);
    setShowEditDialog(true);
  };

  const handleUpdateAccount = async () => {
    if (!editingAccount || !email) {
      setError("Email is required.");
      return;
    }
    if (password) {
      const passwordError = validatePassword(password);
      if (passwordError) {
        setError(passwordError);
        return;
      }
    }

    const emailExists = accounts.find(
      acc => acc.email.toLowerCase() === email.toLowerCase() && acc._id !== editingAccount._id
    );
    if (emailExists) {
      setError("This email is already in use by another client.");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const updated = await clientAccountApi.update(editingAccount._id!, {
        email: email.toLowerCase(),
        ...(password ? { password } : {}),
      });
      setAccounts(prev => prev.map(acc => (acc._id === updated._id ? updated : acc)));
      if (password && updated._id) {
        setRevealedPasswords(prev => ({ ...prev, [updated._id!]: password }));
      }

      const changedPassword = password;
      setEditingAccount(null);
      setSelectedClient("");
      setEmail("");
      setPassword("");
      setShowEditDialog(false);

      if (changedPassword) {
        openRevealDialog(updated, changedPassword, 'reset');
      } else {
        toast({
          title: "Account Updated",
          description: `Email updated for ${getAccountClientName(updated)}.`,
        });
      }
    } catch (err: any) {
      setError(err?.message || "Failed to update account. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = (account: ClientAccount) => {
    if (!account?._id) return;
    setAccountToDelete(account);
  };

  const confirmDeleteAccount = async () => {
    const accountId = accountToDelete?._id;
    if (!accountId) return;
    setDeleting(true);
    try {
      await clientAccountApi.delete(accountId);
      setAccounts(prev => prev.filter(acc => acc._id !== accountId));
      setRevealedPasswords(prev => {
        const next = { ...prev };
        delete next[accountId];
        return next;
      });
      toast({
        title: "Account Deleted",
        description: "Client account has been deleted successfully.",
      });
      setAccountToDelete(null);
    } catch (err: any) {
      toast({
        title: "Error",
        description: err?.message || "Failed to delete account.",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleResetPassword = (account: ClientAccount) => {
    if (!account?._id) return;
    setAccountToReset(account);
  };

  const confirmResetPassword = async () => {
    const account = accountToReset;
    if (!account?._id) return;
    setResetting(true);
    try {
      const newPass = generateStrongPassword();
      const updated = await clientAccountApi.update(account._id!, { password: newPass });
      setAccounts(prev => prev.map(acc => (acc._id === updated._id ? updated : acc)));
      if (updated._id) {
        setRevealedPasswords(prev => ({ ...prev, [updated._id!]: newPass }));
      }
      setAccountToReset(null);
      openRevealDialog(updated, newPass, 'reset');
    } catch (err: any) {
      toast({
        title: "Error",
        description: err?.message || "Failed to reset password.",
        variant: "destructive",
      });
    } finally {
      setResetting(false);
    }
  };

  const handleTogglePassword = async (account: ClientAccount) => {
    const id = account._id;
    if (!id) return;
    if (revealedPasswords[id]) {
      setRevealedPasswords(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      return;
    }
    setRevealingId(id);
    try {
      const { password: plain } = await clientAccountApi.getPassword(id);
      setRevealedPasswords(prev => ({ ...prev, [id]: plain }));
    } catch (err: any) {
      toast({
        title: 'Could not retrieve password',
        description: err?.message || 'This may be a legacy account; reset to generate a new password.',
        variant: 'destructive',
      });
    } finally {
      setRevealingId(null);
    }
  };

  const handleCopyPassword = async (plain: string) => {
    try {
      await navigator.clipboard.writeText(plain);
      toast({ title: 'Copied', description: 'Password copied to clipboard.' });
    } catch {
      toast({
        title: 'Copy failed',
        description: 'Could not copy to clipboard.',
        variant: 'destructive',
      });
    }
  };

  const filteredAccounts = accounts.filter((account) => {
    const name = getAccountClientName(account).toLowerCase();
    return (
      name.includes(searchQuery.toLowerCase()) ||
      account.email.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const resetForm = () => {
    setSelectedClient("");
    setEmail("");
    setPassword("");
    setError(null);
    setEditingAccount(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Client Accounts</h1>
          <p className="text-muted-foreground">
            Manage client login credentials for accessing their personal system
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="mr-2 h-4 w-4" /> Create Account
        </Button>
      </div>

      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="py-4 flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <strong>Passwords are encrypted on the server</strong> and only revealed to authenticated
            admins on demand. They are never stored in your browser. Click the eye icon to view a
            password, or use <em>Reset Password</em> to generate a new one.
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Client Login Accounts</CardTitle>
          <CardDescription>
            View and manage client email and password credentials
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search by client name or email..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Password</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-4">
                      Loading accounts...
                    </TableCell>
                  </TableRow>
                ) : filteredAccounts.length > 0 ? (
                  filteredAccounts.map((account) => (
                    <TableRow key={account._id}>
                      <TableCell className="font-medium">{getAccountClientName(account)}</TableCell>
                      <TableCell>{account.email}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm">
                            {revealedPasswords[account._id!] ?? '••••••••'}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleTogglePassword(account)}
                            disabled={revealingId === account._id}
                            title={revealedPasswords[account._id!] ? 'Hide password' : 'Show password'}
                          >
                            {revealingId === account._id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : revealedPasswords[account._id!] ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                          {revealedPasswords[account._id!] && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCopyPassword(revealedPasswords[account._id!])}
                              title="Copy password"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditAccount(account)}
                            title="Edit email or password"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleResetPassword(account)}
                            title="Reset password"
                          >
                            <KeyRound className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteAccount(account)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-4 text-muted-foreground">
                      {searchQuery ? 'No accounts found matching your search' : 'No client accounts created yet'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create Account Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => {
        if (!open) {
          resetForm();
          setShowCreateDialog(false);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Client Account</DialogTitle>
            <DialogDescription>
              Set up login credentials for a client. The password will be shown once after creation.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Select Client</Label>
              <Select value={selectedClient} onValueChange={setSelectedClient}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a client" />
                </SelectTrigger>
                <SelectContent>
                  {clients
                    .filter(client => !accounts.find(acc => getAccountClientId(acc) === client._id))
                    .map((client) => (
                      <SelectItem key={client._id} value={client._id!}>
                        {client.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Email Address</Label>
              <Input
                type="email"
                placeholder="client@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="off"
              />
            </div>

            <div className="space-y-2">
              <Label>Password (min {PASSWORD_MIN_LENGTH} characters)</Label>
              <div className="flex gap-2">
                <Input
                  type="password"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setPassword(generateStrongPassword())}
                  title="Generate strong password"
                >
                  Generate
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              resetForm();
              setShowCreateDialog(false);
            }}>
              Cancel
            </Button>
            <Button onClick={handleCreateAccount} disabled={saving}>
              {saving ? "Creating..." : "Create Account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Account Dialog */}
      <Dialog open={showEditDialog} onOpenChange={(open) => {
        if (!open) {
          resetForm();
          setShowEditDialog(false);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Client Account</DialogTitle>
            <DialogDescription>
              Update login credentials for {editingAccount ? getAccountClientName(editingAccount) : ''}.
              Leave password blank to keep it unchanged.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Client</Label>
              <Input
                value={editingAccount ? getAccountClientName(editingAccount) : ''}
                disabled
                className="bg-gray-50"
              />
            </div>

            <div className="space-y-2">
              <Label>Email Address</Label>
              <Input
                type="email"
                placeholder="client@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="off"
              />
            </div>

            <div className="space-y-2">
              <Label>New Password (optional)</Label>
              <div className="flex gap-2">
                <Input
                  type="password"
                  placeholder="Leave blank to keep current"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setPassword(generateStrongPassword())}
                >
                  Generate
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              resetForm();
              setShowEditDialog(false);
            }}>
              Cancel
            </Button>
            <Button onClick={handleUpdateAccount} disabled={saving}>
              {saving ? "Updating..." : "Update Account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* One-time password reveal dialog (after create or reset) */}
      <Dialog open={showRevealDialog} onOpenChange={(open) => {
        if (!open) {
          setShowRevealDialog(false);
          setRevealAccount(null);
          setRevealPassword("");
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {revealMode === 'created' ? 'Account Created' : 'New Password Generated'}
            </DialogTitle>
            <DialogDescription>
              Copy this password now. For security, it will <strong>never be shown again</strong>.
              The server only stores the hashed value.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Client</Label>
            <Input
              value={revealAccount ? getAccountClientName(revealAccount) : ''}
              disabled
              className="bg-gray-50"
            />
            <Label>Password</Label>
            <div className="flex items-center gap-2">
              <Input value={revealPassword} readOnly className="font-mono" />
              <Button
                type="button"
                variant="secondary"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(revealPassword);
                    toast({ title: 'Copied', description: 'Password copied to clipboard.' });
                  } catch {
                    toast({
                      title: 'Copy failed',
                      description: 'Could not copy to clipboard.',
                      variant: 'destructive',
                    });
                  }
                }}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowRevealDialog(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Account Confirmation */}
      <AlertDialog
        open={!!accountToDelete}
        onOpenChange={(open) => {
          if (!open) setAccountToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete client account?</AlertDialogTitle>
            <AlertDialogDescription>
              {accountToDelete
                ? `This will permanently delete the login account for ${getAccountClientName(accountToDelete)} (${accountToDelete.email}). The client will no longer be able to log in. This action cannot be undone.`
                : "This action cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                confirmDeleteAccount();
              }}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Password Confirmation */}
      <AlertDialog
        open={!!accountToReset}
        onOpenChange={(open) => {
          if (!open) setAccountToReset(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset password?</AlertDialogTitle>
            <AlertDialogDescription>
              {accountToReset
                ? `Generate a new password for ${getAccountClientName(accountToReset)}? This will replace the current password and the client will need the new one to log in. The new password will be shown to you exactly once.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resetting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                confirmResetPassword();
              }}
              disabled={resetting}
            >
              {resetting ? "Resetting..." : "Reset Password"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Accounts;
